/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { TFunction } from 'i18next';
import type { LeaseState } from '../../constants/lease-states';
import type { Lease } from '../../types/lease.types';

/** Rent brackets, in cents, offered as one-click ranges. */
export const RENT_BRACKETS = [
  { id: 'upTo1500', min: 0, max: 150000 },
  { id: 'from1500To3000', min: 150000, max: 300000 },
  { id: 'from3000To6000', min: 300000, max: 600000 },
  { id: 'above6000', min: 600000, max: Number.POSITIVE_INFINITY },
] as const;

export type RentBracketId = (typeof RENT_BRACKETS)[number]['id'];

/** Due-day groups, matching how contracts are actually written. */
export const DUE_DAY_GROUPS = [
  { id: 'early', days: [1, 2, 3, 4, 5] },
  { id: 'mid', days: [10, 15, 20] },
  { id: 'late', days: [25, 28, 30, 31] },
] as const;

export type DueDayGroupId = (typeof DUE_DAY_GROUPS)[number]['id'];

export interface LeaseFilters {
  states?: LeaseState[];
  brokers?: string[];
  rentBrackets?: RentBracketId[];
  dueDayGroups?: DueDayGroupId[];
  /**
   * Contracts whose term ends within this many days. Set from the URL by
   * the overview's "contratos vencendo" tile, which is the only thing that
   * writes it today -- the panel has no section for it.
   */
  endingWithinDays?: number;
}

/**
 * The rent a contract charges, in cents.
 *
 * Reads the `aluguel` line of the schedule, which is the one activation
 * requires. Seeded contracts carry a minimal line with only `kind` and
 * `amount_cents`, so nothing else may be assumed present.
 */
export function leaseRentCents(lease: Lease): number | null {
  const line = (lease.charge_schedule ?? []).find((l) => l.kind === 'aluguel');
  return typeof line?.amount_cents === 'number' ? line.amount_cents : null;
}

/**
 * The day of the month a contract falls due.
 *
 * Both spellings are read because both are stored: leases opened through
 * this UI carry `payment_day` (the only field the backend's DueDatePolicy
 * declares), while older and seeded ones carry `day_of_month`. Reading one
 * alone would silently miss half the registry.
 */
export function leaseDueDay(lease: Lease): number | null {
  const policy = lease.due_date_policy;
  return policy?.payment_day ?? policy?.day_of_month ?? null;
}

/**
 * Whether the rent-bracket filter accepts this lease (OR across brackets).
 */
function matchesRent(lease: Lease, brackets: readonly RentBracketId[]): boolean {
  const cents = leaseRentCents(lease);
  if (cents === null) return false;
  return RENT_BRACKETS.some(
    (b) => brackets.includes(b.id) && cents >= b.min && cents < b.max,
  );
}

/**
 * Whether the due-day filter accepts this lease (OR across day groups).
 */
function matchesDueDay(lease: Lease, groups: readonly DueDayGroupId[]): boolean {
  const day = leaseDueDay(lease);
  if (day === null) return false;
  return DUE_DAY_GROUPS.some(
    (g) => groups.includes(g.id) && (g.days as readonly number[]).includes(day),
  );
}

/**
 * Days from today until a contract's term ends, or null when it has none.
 *
 * A lease may be recorded without an end date, which is a different fact
 * from ending far away: the dashboard counts those separately rather than
 * folding them in, and so does this.
 */
export function leaseDaysUntilEnd(lease: Lease, today = new Date()): number | null {
  if (!lease.end_date) return null;
  const end = new Date(`${lease.end_date.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(end.getTime())) return null;
  const midnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((end.getTime() - midnight.getTime()) / 86400000);
}

/**
 * Whether the state filter (if active) accepts this lease.
 */
function passesStateFilter(lease: Lease, filters: LeaseFilters): boolean {
  return !filters.states?.length || filters.states.includes(lease.state);
}

/**
 * Whether the broker filter (if active) accepts this lease.
 */
function passesBrokerFilter(lease: Lease, filters: LeaseFilters): boolean {
  return !filters.brokers?.length || filters.brokers.includes(lease.responsible_broker_email);
}

/**
 * Whether the rent-bracket filter (if active) accepts this lease.
 */
function passesRentFilter(lease: Lease, filters: LeaseFilters): boolean {
  return !filters.rentBrackets?.length || matchesRent(lease, filters.rentBrackets);
}

/**
 * Whether the due-day filter (if active) accepts this lease.
 */
function passesDueDayFilter(lease: Lease, filters: LeaseFilters): boolean {
  return !filters.dueDayGroups?.length || matchesDueDay(lease, filters.dueDayGroups);
}

/**
 * Whether the expiry filter (if active) accepts this lease.
 *
 * A term already past counts as expiring: the contract is still running
 * and is the most urgent of the set, so hiding it would drop exactly the
 * rows the tile promises.
 */
function passesExpiryFilter(lease: Lease, filters: LeaseFilters): boolean {
  if (filters.endingWithinDays === undefined) return true;
  const days = leaseDaysUntilEnd(lease);
  return days !== null && days <= filters.endingWithinDays;
}

/**
 * Whether a lease clears every active filter section.
 */
function matchesLeaseFilters(lease: Lease, filters: LeaseFilters): boolean {
  return passesStateFilter(lease, filters)
    && passesBrokerFilter(lease, filters)
    && passesRentFilter(lease, filters)
    && passesDueDayFilter(lease, filters)
    && passesExpiryFilter(lease, filters);
}

/**
 * Apply *filters* to *leases*.
 *
 * Each section narrows (AND between sections), while choices inside one
 * section widen (OR) -- the behaviour every other filter panel in the app
 * already has, so the same clicks mean the same thing here.
 */
export function applyLeaseFilters(leases: Lease[], filters: LeaseFilters): Lease[] {
  return leases.filter((lease) => matchesLeaseFilters(lease, filters));
}

/**
 * How many individual choices are active, across every section.
 */
export function countLeaseFilters(filters: LeaseFilters): number {
  return (filters.states?.length ?? 0)
    + (filters.brokers?.length ?? 0)
    + (filters.rentBrackets?.length ?? 0)
    + (filters.dueDayGroups?.length ?? 0)
    + (filters.endingWithinDays === undefined ? 0 : 1);
}

/**
 * One removable chip label per active choice.
 *
 * Labels are the chip's identity as well as its text -- `PageHeader` hands
 * the clicked label straight back to `onRemoveFilter` -- so they have to
 * stay unique across sections. The broker's own e-mail and the localized
 * range labels already are.
 */
export function buildLeaseFilterLabels(
  filters: LeaseFilters,
  t: TFunction,
  stateLabel: (state: string) => string,
): string[] {
  return [
    ...(filters.states ?? []).map(stateLabel),
    ...(filters.brokers ?? []),
    ...(filters.rentBrackets ?? []).map((id) => t(`filters.rentBrackets.${id}`)),
    ...(filters.dueDayGroups ?? []).map((id) => t(`filters.dueDayGroups.${id}`)),
    ...(filters.endingWithinDays === undefined
      ? []
      : [t('filters.endingWithin', { days: filters.endingWithinDays })]),
  ];
}

/**
 * Drop whichever choice produced *label*.
 *
 * Compares against the same rendered labels `buildLeaseFilterLabels`
 * emits, so a chip removes exactly the choice it displays.
 */
export function removeLeaseFilterByLabel(
  filters: LeaseFilters,
  label: string,
  t: TFunction,
  stateLabel: (state: string) => string,
): LeaseFilters {
  return {
    states: (filters.states ?? []).filter((s) => stateLabel(s) !== label),
    brokers: (filters.brokers ?? []).filter((b) => b !== label),
    rentBrackets: (filters.rentBrackets ?? [])
      .filter((id) => t(`filters.rentBrackets.${id}`) !== label),
    dueDayGroups: (filters.dueDayGroups ?? [])
      .filter((id) => t(`filters.dueDayGroups.${id}`) !== label),
    endingWithinDays:
      t('filters.endingWithin', { days: filters.endingWithinDays }) === label
        ? undefined
        : filters.endingWithinDays,
  };
}

/**
 * The brokers who actually answer for a lease, sorted.
 *
 * Derived from the registry rather than from the agency roster: a filter
 * offering members with no leases would guarantee empty results, and
 * leases whose broker has since left the agency would be unreachable.
 */
export function leaseBrokerOptions(leases: Lease[]): string[] {
  const emails = new Set(
    leases.map((l) => l.responsible_broker_email).filter(Boolean),
  );
  return [...emails].sort((a, b) => a.localeCompare(b));
}
